import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, ArrowRight, MapPin, User, Leaf, ShoppingCart, Info, Globe } from 'lucide-react';

const OnionsCatalogue = () => {
  const [selectedVariety, setSelectedVariety] = useState(null);

  const onionVarieties = [
    {
      id: 'red-nashik',
      name: 'Nashik Red Onions',
      subtitle: 'Export Quality - Dark Red',
      image: 'https://images.pexels.com/photos/4197447/pexels-photo-4197447.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹25-40',
      unit: 'per kg',
      minOrder: '1 tonne',
      season: 'Year-round',
      origin: 'Nashik, Maharashtra',
      exportGrade: true,
      organic: false,
      grade: 'Export Grade A',
      size: '45-65mm',
      shelfLife: '3-4 months',
      description: 'Premium Nashik red onions known worldwide for their pungent flavor, deep red color, and excellent storage life. The most exported Indian onion variety.',
      features: ['Deep red color', 'High pungency', 'Long shelf life', 'Export quality', 'Low moisture'],
      farmer: {
        name: 'Balasaheb Pawar',
        location: 'Lasalgaon, Nashik',
        image: 'https://images.pexels.com/photos/2804327/pexels-photo-2804327.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '35 years',
        farmSize: '50 acres',
        story: 'Lasalgaon is Asia\'s largest onion market, and our family has been growing onions here for three generations. We supply to exporters across India.'
      },
      certifications: ['APEDA Certified', 'GlobalGAP', 'Phytosanitary Certified'],
      exportMarkets: ['UAE', 'Malaysia', 'Singapore', 'Bangladesh', 'Sri Lanka']
    },
    {
      id: 'white-onion',
      name: 'White Onions',
      subtitle: 'Mild Flavor - Premium Quality',
      image: 'https://images.pexels.com/photos/7890176/pexels-photo-7890176.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹30-45',
      unit: 'per kg',
      minOrder: '500 kg',
      season: 'November - April',
      origin: 'Ahmednagar, Maharashtra',
      exportGrade: true,
      organic: false,
      grade: 'Grade A',
      size: '40-60mm',
      shelfLife: '2-3 months',
      description: 'Mild-flavored white onions preferred for salads, Mexican cuisine, and gourmet cooking. Clean appearance with papery white skin.',
      features: ['Mild flavor', 'White skin', 'Salad grade', 'Low pungency', 'Gourmet quality'],
      farmer: {
        name: 'Dnyaneshwar Jadhav',
        location: 'Ahmednagar, Maharashtra',
        image: 'https://images.pexels.com/photos/5717546/pexels-photo-5717546.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '20 years',
        farmSize: '30 acres',
        story: 'White onions require special care and the right soil conditions. Our farm has been perfecting white onion cultivation for two decades.'
      },
      certifications: ['FSSAI', 'APEDA'],
      exportMarkets: ['UAE', 'UK', 'USA']
    },
    {
      id: 'pink-onion',
      name: 'Pink Onions',
      subtitle: 'Rose Pink - Premium Selection',
      image: 'https://images.pexels.com/photos/4197444/pexels-photo-4197444.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹28-42',
      unit: 'per kg',
      minOrder: '1 tonne',
      season: 'Year-round',
      origin: 'Solapur, Maharashtra',
      exportGrade: true,
      organic: true,
      grade: 'Export Grade',
      size: '50-70mm',
      shelfLife: '4-5 months',
      description: 'Beautiful rose-pink onions with excellent keeping quality. Balanced flavor profile - not too mild, not too pungent. Preferred in European markets.',
      features: ['Rose pink color', 'Balanced flavor', 'Excellent storage', 'European export', 'Uniform size'],
      farmer: {
        name: 'Vitthal Shinde',
        location: 'Solapur, Maharashtra',
        image: 'https://images.pexels.com/photos/5704849/pexels-photo-5704849.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '28 years',
        farmSize: '40 acres',
        story: 'Pink onions are a specialty variety that fetches premium prices in export markets. We\'ve invested in proper curing and storage facilities.'
      },
      certifications: ['Organic India', 'GlobalGAP', 'APEDA'],
      exportMarkets: ['Netherlands', 'Germany', 'UK', 'UAE']
    },
    {
      id: 'shallots',
      name: 'Shallots (Sambar Onions)',
      subtitle: 'Small Onions - South Indian',
      image: 'https://images.pexels.com/photos/4197439/pexels-photo-4197439.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹60-80',
      unit: 'per kg',
      minOrder: '200 kg',
      season: 'Year-round',
      origin: 'Trichy, Tamil Nadu',
      exportGrade: false,
      organic: true,
      grade: 'Premium',
      size: '20-35mm',
      shelfLife: '2 months',
      description: 'Small, flavorful shallots essential for South Indian cuisine. Rich taste with slight sweetness, perfect for sambar, chutneys, and pickles.',
      features: ['Intense flavor', 'Small size', 'Traditional variety', 'Pickle grade', 'Rich aroma'],
      farmer: {
        name: 'Selvam K',
        location: 'Trichy, Tamil Nadu',
        image: 'https://images.pexels.com/photos/5717628/pexels-photo-5717628.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '25 years',
        farmSize: '15 acres',
        story: 'Shallots have been cultivated in Tamil Nadu for centuries. Our family continues the tradition of growing the finest small onions.'
      },
      certifications: ['Organic Certified', 'FSSAI'],
      exportMarkets: []
    },
    {
      id: 'bangalore-rose',
      name: 'Bangalore Rose Onions',
      subtitle: 'Karnataka Specialty',
      image: 'https://images.pexels.com/photos/7890089/pexels-photo-7890089.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹35-50',
      unit: 'per kg',
      minOrder: '500 kg',
      season: 'October - March',
      origin: 'Chitradurga, Karnataka',
      exportGrade: true,
      organic: false,
      grade: 'Grade A',
      size: '55-75mm',
      shelfLife: '3-4 months',
      description: 'Large, beautiful rose-colored onions from Karnataka. Known for their mild sweetness and versatility in cooking.',
      features: ['Large size', 'Rose color', 'Mild sweet', 'Versatile', 'Good storage'],
      farmer: {
        name: 'Basavaraj H',
        location: 'Chitradurga, Karnataka',
        image: 'https://images.pexels.com/photos/2382665/pexels-photo-2382665.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '22 years',
        farmSize: '35 acres',
        story: 'The dry climate of Chitradurga is perfect for growing onions with excellent storage quality. Our rose onions are famous across South India.'
      },
      certifications: ['APEDA', 'FSSAI'],
      exportMarkets: ['Malaysia', 'Singapore']
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/4197447/pexels-photo-4197447.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Onion fields"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#263776]/95 via-[#263776]/70 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <Link to="/products/orivana-fresh" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to ORIVANA Fresh
          </Link>
          <Badge className="bg-[#F47D20] text-white mb-4 block w-fit">
            <Globe className="w-3 h-3 mr-1 inline" /> Export Quality
          </Badge>
          <h1 className="text-5xl font-bold text-white mb-4">Onions</h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Premium Indian onions - Red, White, Pink varieties. Export-grade quality from Maharashtra and Karnataka's finest farms.
          </p>
        </div>
      </section>

      {/* Export Banner */}
      <section className="bg-[#F47D20] py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-8 text-white text-sm flex-wrap">
            <span className="flex items-center"><Globe className="w-4 h-4 mr-2" /> Exporting to 15+ Countries</span>
            <span>|</span>
            <span>APEDA & GlobalGAP Certified</span>
            <span>|</span>
            <span>Phytosanitary Compliant</span>
          </div>
        </div>
      </section>

      {/* Varieties Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Varieties List */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-[#263776] mb-6">Select Variety</h2>
              <div className="space-y-4">
                {onionVarieties.map((variety) => (
                  <Card 
                    key={variety.id}
                    className={`cursor-pointer transition-all duration-300 ${
                      selectedVariety?.id === variety.id 
                        ? 'ring-2 ring-[#F47D20] shadow-lg' 
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => setSelectedVariety(variety)}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <img 
                        src={variety.image} 
                        alt={variety.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-[#263776]">{variety.name}</h3>
                          {variety.exportGrade && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">Export</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{variety.origin}</p>
                        <p className="text-[#F47D20] font-bold mt-1">{variety.price} <span className="text-xs text-slate-400">{variety.unit}</span></p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Right: Selected Variety Details */}
            <div className="lg:col-span-2">
              {selectedVariety ? (
                <div className="sticky top-4">
                  <div className="relative rounded-xl overflow-hidden h-[350px] mb-6">
                    <img 
                      src={selectedVariety.image} 
                      alt={selectedVariety.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                      <Badge className="bg-[#263776] text-white">{selectedVariety.grade}</Badge>
                      {selectedVariety.exportGrade && (
                        <Badge className="bg-blue-600 text-white">
                          <Globe className="w-3 h-3 mr-1" /> Export Grade
                        </Badge>
                      )}
                      {selectedVariety.organic && (
                        <Badge className="bg-green-600 text-white">
                          <Leaf className="w-3 h-3 mr-1" /> Organic
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h2 className="text-3xl font-bold text-[#263776] mb-2">{selectedVariety.name}</h2>
                      <p className="text-lg text-[#F47D20] font-medium mb-4">{selectedVariety.subtitle}</p>
                      <p className="text-slate-600 mb-4">{selectedVariety.description}</p>
                      
                      <div className="bg-slate-50 rounded-lg p-4 mb-4">
                        <h4 className="font-bold text-[#263776] mb-3 flex items-center">
                          <Info className="w-4 h-4 mr-2" /> Specifications
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-slate-500">Season:</span> <span className="font-medium">{selectedVariety.season}</span></div>
                          <div><span className="text-slate-500">Origin:</span> <span className="font-medium">{selectedVariety.origin}</span></div>
                          <div><span className="text-slate-500">Size:</span> <span className="font-medium">{selectedVariety.size}</span></div>
                          <div><span className="text-slate-500">Shelf Life:</span> <span className="font-medium">{selectedVariety.shelfLife}</span></div>
                          <div><span className="text-slate-500">Min Order:</span> <span className="font-medium">{selectedVariety.minOrder}</span></div>
                          <div><span className="text-slate-500">Grade:</span> <span className="font-medium">{selectedVariety.grade}</span></div>
                        </div>
                      </div>

                      {selectedVariety.exportMarkets.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-bold text-[#263776] mb-2">Export Markets</h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedVariety.exportMarkets.map((market, idx) => (
                              <Badge key={idx} className="bg-blue-100 text-blue-700">
                                {market}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <h4 className="font-bold text-[#263776] mb-2">Certifications</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedVariety.certifications.map((cert, idx) => (
                            <Badge key={idx} className="bg-[#263776]/10 text-[#263776]">
                              {cert}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <Card className="mb-6 border-2 border-[#F47D20]/20">
                        <CardContent className="p-6">
                          <div className="text-3xl font-bold text-[#F47D20] mb-1">{selectedVariety.price}</div>
                          <div className="text-slate-500 mb-4">{selectedVariety.unit} (Wholesale/Export)</div>
                          <div className="text-sm text-slate-600 mb-4">
                            Minimum Order: <span className="font-bold">{selectedVariety.minOrder}</span>
                          </div>
                          <Button asChild className="w-full bg-[#F47D20] hover:bg-[#d66b1a] mb-3">
                            <Link to={`/products/orivana-fresh/enquiry?product=${selectedVariety.id}&category=onions`}>
                              <ShoppingCart className="w-4 h-4 mr-2" />
                              Request Quote
                            </Link>
                          </Button>
                          <Button asChild variant="outline" className="w-full border-[#263776] text-[#263776]">
                            <Link to="/contact">
                              Contact Export Team
                            </Link>
                          </Button>
                        </CardContent>
                      </Card>

                      <Card className="bg-gradient-to-br from-[#263776]/5 to-[#F47D20]/5">
                        <CardContent className="p-6">
                          <h4 className="font-bold text-[#263776] mb-4 flex items-center">
                            <User className="w-4 h-4 mr-2" /> Meet the Farmer
                          </h4>
                          <div className="flex items-start gap-4">
                            <img 
                              src={selectedVariety.farmer.image}
                              alt={selectedVariety.farmer.name}
                              className="w-20 h-20 rounded-full object-cover"
                            />
                            <div>
                              <h5 className="font-bold text-[#263776]">{selectedVariety.farmer.name}</h5>
                              <div className="flex items-center text-sm text-slate-500 mb-2">
                                <MapPin className="w-3 h-3 mr-1" />
                                {selectedVariety.farmer.location}
                              </div>
                              <div className="text-xs text-slate-400 mb-2">
                                {selectedVariety.farmer.experience} experience • {selectedVariety.farmer.farmSize}
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 mt-4 italic">
                            "{selectedVariety.farmer.story}"
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[500px] bg-slate-50 rounded-xl">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-[#F47D20]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Globe className="w-8 h-8 text-[#F47D20]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#263776] mb-2">Select a Variety</h3>
                    <p className="text-slate-500">Click on an onion variety to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-[#263776]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Looking for Export-Grade Onions?</h2>
          <p className="text-white/80 mb-6">We handle documentation, phytosanitary certificates, and logistics for international shipments</p>
          <Button asChild size="lg" className="bg-[#F47D20] hover:bg-[#d66b1a]">
            <Link to="/products/orivana-fresh/enquiry">Get Export Quote</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default OnionsCatalogue;
